import React from 'react';
import { useDrop } from 'react-dnd';
import { KanbanDeal } from '../types/api';
import KanbanDealCard, { KANBAN_DEAL_TYPE } from './KanbanDealCard';

interface KanbanColumnProps {
  status: string;
  deals: KanbanDeal[];
  totalAmount: number;
  expandedCards: Set<string>;
  onToggleCardExpand: (dealId: string) => void;
  onLaunchAssistant: (deal: KanbanDeal) => void;
  onCardClick: (deal: KanbanDeal) => void;
  onDealDrop: (deal: KanbanDeal, newStatus: string) => void;
}

const formatCurrency = (amount?: number) => {
  if (!amount) return '$0';
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

const KanbanColumn: React.FC<KanbanColumnProps> = ({
  status,
  deals,
  totalAmount,
  expandedCards,
  onToggleCardExpand,
  onLaunchAssistant,
  onCardClick,
  onDealDrop
}) => {
  const [{ isOver, canDrop }, dropRef] = useDrop(() => ({
    accept: KANBAN_DEAL_TYPE,
    drop: (item: { deal: KanbanDeal }) => {
      if (item.deal.status !== status) {
        onDealDrop(item.deal, status);
      }
    },
    canDrop: (item: { deal: KanbanDeal }) => item.deal.status !== status,
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  }), [status, onDealDrop]);

  const dropHighlight = isOver && canDrop;

  return (
    <div
      ref={dropRef as unknown as React.Ref<HTMLDivElement>}
      style={{
        width: 310,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        background: dropHighlight ? 'rgba(var(--obsidian-accent-rgb, 138, 180, 248), 0.08)' : 'var(--obsidian-bg)',
        borderRadius: 8,
        border: dropHighlight
          ? '2px solid var(--obsidian-accent)'
          : '1px solid var(--obsidian-border)',
        overflow: 'hidden',
        transition: 'background 0.15s ease, border-color 0.15s ease',
      }}
    >
      {/* Column header */}
      <div style={{
        padding: '12px 14px',
        borderBottom: '1px solid var(--obsidian-border)',
        background: 'rgba(255,255,255,0.02)'
      }}>
        <div style={{
          fontSize: 14,
          fontWeight: 600,
          color: 'var(--obsidian-text-primary)',
          marginBottom: 4
        }}>
          {status}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{
            fontSize: 11,
            fontWeight: 600,
            color: 'var(--obsidian-text-secondary)',
            background: 'rgba(255,255,255,0.08)',
            borderRadius: 10,
            padding: '2px 8px',
            lineHeight: '16px'
          }}>
            {deals.length}
          </span>
          <span style={{
            fontSize: 12,
            color: 'var(--obsidian-text-secondary)'
          }}>
            {formatCurrency(totalAmount)}
          </span>
        </div>
      </div>

      {/* Column body */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: 8,
        minHeight: 100
      }}>
        {deals.length > 0 ? (
          deals.map(deal => (
            <KanbanDealCard
              key={deal.id}
              deal={deal}
              isExpanded={expandedCards.has(deal.id)}
              onToggleExpand={() => onToggleCardExpand(deal.id)}
              onLaunchAssistant={() => onLaunchAssistant(deal)}
              onCardClick={() => onCardClick(deal)}
            />
          ))
        ) : (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '32px 16px',
            color: 'var(--obsidian-text-secondary)'
          }}>
            <span className="material-symbols-outlined" style={{ fontSize: 32, opacity: 0.3, marginBottom: 8 }}>
              inbox
            </span>
            <span style={{ fontSize: 12, opacity: 0.5 }}>
              {isOver && canDrop ? 'Drop here' : 'No deals'}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(KanbanColumn);
